import type {
	AlgoliaMultipleQueriesQuery,
	InstantMeiliSearchInstance,
} from '@meilisearch/instant-meilisearch';
import { instantMeiliSearch } from '@meilisearch/instant-meilisearch';
import * as Sentry from '@sentry/react';
import { getGetKeyQueryKey, useGetKey, useMetaData } from '@uturn/api/v1';
import { useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { InstantSearch } from 'react-instantsearch';
import { SearchReadyProvider } from './search-ready-context';

export const SearchProvider = ({ children }: { children: React.ReactNode }) => {
	const { data } = useMetaData();
	const userId = data?.data.user?.id;

	const initialQueryKey = getGetKeyQueryKey();
	const { data: result } = useGetKey({
		query: {
			staleTime: 600000, // 10 min.
			queryKey: [...initialQueryKey, data?.data.user?.id],
		},
	});

	const searchClient = useMemo(() => {
		if (!result?.data || !userId) {
			return undefined;
		}

		const baseClient = instantMeiliSearch(
			import.meta.env.VITE_MEILISEARCH_HOST,
			result.data,
			// Identifies the user in Meilisearch search analytics. Without it
			// Meilisearch generates a fresh id per request, so every search
			// looks like a distinct user
			userId
				? { requestConfig: { headers: { 'X-MS-USER-ID': String(userId) } } }
				: undefined,
		);

		// Wrap search method to catch async errors and return empty results
		const wrappedClient: InstantMeiliSearchInstance = {
			...baseClient,
			search: async (requests: AlgoliaMultipleQueriesQuery[]) => {
				try {
					return await baseClient.search(requests);
				} catch (error) {
					Sentry.captureException(error, {
						tags: { context: 'MeiliSearch' },
					});

					// Return empty results to gracefully degrade search functionality
					return {
						results: requests.map(() => ({
							hits: [],
							nbHits: 0,
							page: 0,
							nbPages: 0,
							hitsPerPage: 0,
							processingTimeMS: 0,
							query: '',
							params: '',
						})),
					};
				}
			},
		};

		return wrappedClient;
	}, [result?.data, userId]);

	return (
		<ErrorBoundary
			fallback={children}
			onError={(error) => {
				Sentry.captureException(error, {
					tags: { context: 'SearchProvider' },
				});
			}}
		>
			<SearchReadyProvider value={!!searchClient}>
				{searchClient ? (
					<InstantSearch
						indexName={`${import.meta.env.VITE_MEILISEARCH_INDEX}`}
						searchClient={searchClient}
					>
						{children}
					</InstantSearch>
				) : (
					children
				)}
			</SearchReadyProvider>
		</ErrorBoundary>
	);
};
